declare const __APP_VERSION__: string;

const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_WINDOW_MS = 60_000;
const SESSION_KEY = "__omnisiq_version_reload";

interface ReloadState {
  count: number;
  lastAttempt: number;
  targetVersion: string;
}

function getReloadState(): ReloadState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return { count: 0, lastAttempt: 0, targetVersion: "" };
    return JSON.parse(raw);
  } catch {
    return { count: 0, lastAttempt: 0, targetVersion: "" };
  }
}

function setReloadState(state: ReloadState): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable
  }
}

function clearReloadState(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // no-op
  }
}

/**
 * Fetches version.json bypassing all caches and compares the deployed
 * version against the version baked into this JS bundle at build time.
 *
 * Returns `true` if the page should reload (version mismatch detected
 * and reload is safe). Returns `false` if no reload is needed or if
 * reloading would risk a loop.
 */
export async function checkForUpdate(): Promise<boolean> {
  if (__APP_VERSION__ === "dev") return false;

  try {
    const resp = await fetch("./version.json", {
      cache: "no-store",
      headers: { Pragma: "no-cache" },
    });

    if (!resp.ok) return false;

    const data = await resp.json();
    const serverVersion: string | undefined = data.version;
    if (!serverVersion) return false;

    if (serverVersion === __APP_VERSION__) {
      clearReloadState();
      return false;
    }

    // Version mismatch
    const state = getReloadState();
    const now = Date.now();

    if (state.targetVersion !== serverVersion || now - state.lastAttempt > RELOAD_WINDOW_MS) {
      setReloadState({ count: 1, lastAttempt: now, targetVersion: serverVersion });
      return true;
    }

    if (state.count < MAX_RELOAD_ATTEMPTS) {
      setReloadState({ count: state.count + 1, lastAttempt: now, targetVersion: serverVersion });
      return true;
    }

    // Max attempts reached — prevent infinite loop
    console.warn(
      `[VersionCheck] Max reload attempts (${MAX_RELOAD_ATTEMPTS}) reached for version ${serverVersion}. Running stale version ${__APP_VERSION__}.`
    );
    return false;
  } catch {
    return false;
  }
}

/**
 * Lightweight check for periodic polling. Does NOT manage reload state.
 */
export async function isUpdateAvailable(): Promise<boolean> {
  try {
    const resp = await fetch("./version.json", {
      cache: "no-store",
      headers: { Pragma: "no-cache" },
    });
    if (!resp.ok) return false;
    const data = await resp.json();
    return Boolean(data.version && data.version !== __APP_VERSION__);
  } catch {
    return false;
  }
}

/**
 * Force reload using a cache-busting query parameter.
 * Azure Blob Storage ignores query params, but the browser treats
 * the URL as new and bypasses its HTTP cache.
 */
export function forceReload(): void {
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  window.location.replace(url.toString());
}
